import React from "react";
import ResourceCard from "../polaris-components/ResourceCard";
import VideoLightbox from "../polaris-components/VideoLightbox";
import LaunchModal from "../polaris-components/LaunchModal";
import CompletionModal from "../polaris-components/CompletionModal";
import ExpertModal from "../polaris-components/ExpertModal";
import { fetchResources, markCourseCompleted } from "../utils/resources";
import { courseVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import { ResourceDto } from "../types/resources";
import { useAuth } from "../contexts/AuthContext";
import "../styles/resources.css";
import "../styles/course-cards.css";
import "../styles/pagination.css";

type ModalState = {
    type: 'launch' | 'video' | 'completion' | 'expert' | null;
    resource: ResourceDto | null;
};

/** Show every resource on one page when the catalog is small (e.g. 8 items). */
const RESOURCES_SINGLE_PAGE_MAX = 12;

function useResourcesItemsPerPage(totalItems: number) {
    const [viewportPageSize, setViewportPageSize] = React.useState(8);

    React.useEffect(() => {
        const update = () => {
            const width = window.innerWidth;
            if (width >= 1280) {
                setViewportPageSize(8); /* 4 columns × 2 rows */
            } else if (width >= 900) {
                setViewportPageSize(9); /* 3 columns × 3 rows */
            } else if (width >= 640) {
                setViewportPageSize(8); /* 2 columns × 4 rows */
            } else {
                setViewportPageSize(6); /* 1 column */
            }
        };

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    if (totalItems <= RESOURCES_SINGLE_PAGE_MAX) {
        return Math.max(totalItems, 1);
    }

    return viewportPageSize;
}

export default function ResourcesPage(){
    const { userInfo } = useAuth();
    const [items, setItems] = React.useState<ResourceDto[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [modalState, setModalState] = React.useState<ModalState>({ type: null, resource: null });
    const [currentPage, setCurrentPage] = React.useState(1);

    const itemsPerPage = useResourcesItemsPerPage(items.length);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage, items.length]);

    const role = userInfo?.role || localStorage.getItem('userRole');
    const isAthleteUser = role
        ? (role.toLowerCase().includes('athlete') || role.toLowerCase().includes('student'))
        : false;
    const isAdminOrStaff = !isAthleteUser;

    React.useEffect(() => {
        async function loadResources() {
            try {
                setLoading(true);
                setError(null);
                const resources = await fetchResources();
                setItems(resources);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load resources");
            } finally {
                setLoading(false);
            }
        }
        loadResources();
    }, []);

    function handleLaunch(resource: ResourceDto) {
        // Card-level launch is only invoked for courses with no modules; courses with
        // modules navigate to the detail page directly from ResourceCard.
        setModalState({ type: 'launch', resource });
    }

    function handleLaunchVideo() {
        if (modalState.resource) {
            setModalState({ type: 'video', resource: modalState.resource });
        }
    }

    function handleVideoEnd(resourceId: number) {
        // Persist course completion. The video lightbox here is only ever opened for
        // courses without modules, so finishing the video means finishing the course.
        // Optimistically reflect completion in local state; reconcile on next page load.
        setItems(prev => prev.map(r =>
            r.id === resourceId ? { ...r, isCompleted: true } : r
        ));
        markCourseCompleted(resourceId).catch(() => {
            // Silently ignore; will reconcile on next reload.
        });

        if (isAdminOrStaff) {
            closeModal();
            return;
        }
        const resource = items.find(r => r.id === resourceId);
        if (resource) {
            setModalState({ type: 'completion', resource });
        }
    }

    function handleExpertClick(resource: ResourceDto) {
        setModalState({ type: 'expert', resource });
    }

    function closeModal() {
        setModalState({ type: null, resource: null });
    }

    const totalPages = itemsPerPage > 0 ? Math.ceil(items.length / itemsPerPage) : 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = items.slice(startIndex, endIndex);

    if (loading) {
        return (
            <div className="resources-page">
                <section className="courses-section">
                    <div className="loading-spinner">Loading...</div>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className="resources-page">
                <section className="courses-section">
                    <div className="error-message" style={{color:"#991b1b"}}>Error: {error}</div>
                </section>
            </div>
        );
    }

    return (
        <div className="resources-page">
            <section className="courses-section">
                <div className="courses-content-layout">
                    <div className="courses-grid">
                        {currentItems.map(r => (
                            <ResourceCard
                                key={r.id}
                                resource={r}
                                onLaunch={handleLaunch}
                                onExpertClick={handleExpertClick}
                            />
                        ))}
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <div className="pagination-info">
                            <span>
                                Showing <strong>{startIndex + 1}-{Math.min(endIndex, items.length)}</strong> of <strong>{items.length}</strong> resources
                            </span>
                        </div>

                        <div className="pagination-controls">
                            <div className="page-nav">
                                <button
                                    className="page-btn"
                                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                                    disabled={currentPage <= 1}
                                    aria-label="Previous page"
                                >
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        className={`page-number ${page === currentPage ? "page-number-active" : ""}`}
                                        onClick={() => setCurrentPage(page)}
                                        aria-label={`Page ${page}`}
                                        aria-current={page === currentPage ? 'page' : undefined}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    className="page-btn"
                                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                                    disabled={currentPage >= totalPages}
                                    aria-label="Next page"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {modalState.type === 'launch' && modalState.resource && (
                <LaunchModal
                    resource={modalState.resource}
                    onClose={closeModal}
                    onLaunch={handleLaunchVideo}
                />
            )}

            {modalState.type === 'video' && modalState.resource && (
                <VideoLightbox
                    open={true}
                    resource={modalState.resource}
                    onClose={closeModal}
                    onVideoEnd={handleVideoEnd}
                    analytics={courseVideoAnalytics(WORDPRESS_TAB.RESOURCES, modalState.resource.id)}
                />
            )}

            {modalState.type === 'completion' && modalState.resource && (
                <CompletionModal
                    resource={modalState.resource}
                    onClose={closeModal}
                />
            )}

            {modalState.type === 'expert' && modalState.resource && (
                <ExpertModal
                    expert={modalState.resource.expert}
                    onClose={closeModal}
                />
            )}
        </div>
    );
}
